<template>
  <div class="card" :class="{ create: create }" @click="handleClick">
    <div class="create-container" v-if="create">
      <PlusOutlined class="icon" />
      <span>Crear nueva plantilla</span>
    </div>

    <template v-else>
      <div style="flex: 1 1 auto">
        <header>
          <span class="type">
            {{
              Number(template?.template_type) === TemplateType.Default
                ? `Creado desde ${ProductName}`
                : "Plantilla HTML"
            }}
          </span>

          <a-popover>
            <template #content>
              <div class="actions">
                <a-button type="text" @click="handleEdit">
                  <EditOutlined /> Editar plantilla
                </a-button>

                <a-button type="text" @click="handleEditPortfolio">
                  <EditOutlined /> Editar campañas
                </a-button>

                <a-button type="text" @click="handleCopy">
                  <CopyOutlined /> Duplicar plantilla
                </a-button>

                <a-button type="text" @click="openMailModal = true">
                  <SendOutlined :rotate="-45" /> Enviar correo de prueba
                </a-button>

                <a-button type="text" danger @click="handleDelete">
                  <DeleteOutlined /> Eliminar plantilla
                </a-button>
              </div>
            </template>

            <MoreOutlined style="font-size: 20px" />
          </a-popover>
        </header>

        <h2 class="name">{{ template?.name }}</h2>

        <p class="description">{{ template?.description }}</p>
      </div>

      <div>
        <a-divider type="horizontal" class="divider" />

        <footer>
          <div>
            <a-tag :color="isActive ? 'green' : undefined">
              {{ isActive ? "Activo" : "Inactivo" }}
            </a-tag>
            
            <a-popover v-model:open="visiblePortfoliosPopover" :title="`Campañas asociadas: ${countPortfolios}`" trigger="click">
              <template #content>
                <ul class="portfolios__list">
                  <li v-for="item in firstPortfolios">
                    {{ item.name }}
                  </li>
                </ul>
                <a-divider style="margin: 10px 0" />
                <div class="popover__footer" v-if="countPortfolios > firstPortfolios?.length">
                  <a-button type="link" @click="handleViewPortfolios">
                    <EyeOutlined /> Ver {{ countPortfolios - firstPortfolios?.length }} más
                  </a-button>
                  <a-button type="link" @click="handleEditPortfolio">
                    <EditOutlined /> Editar
                  </a-button>
                </div>
                <div class="popover__footer" v-else>
                  <a-button type="link" @click="handleEditPortfolio">
                    <EditOutlined /> Editar campañas
                  </a-button>
                </div>
              </template>
              <a-tooltip>
                <template #title v-if="countPortfolios == 0">
                  No hay campañas asociadas a esta plantilla de correo. <span class="associate" @click="handleEditPortfolio">Asociar campañas</span>
                </template>
                <a-button type="link" style="padding: 0">
                  <a-tag :color="countPortfolios > 0 ? 'blue' : undefined" class="tag-portfolio">
                    <template #icon>
                      <IconMegaphone :active="countPortfolios > 0" />
                    </template>  
                    Campañas: {{ countPortfolios > 10 ? '+10' : countPortfolios }}
                  </a-tag>
                </a-button>
              </a-tooltip>
            </a-popover>

            <a-tag v-if="template?.deleted_variable" color="warning">
              <WarningFilled /> Variable eliminada
            </a-tag>
          </div>

          <a-switch
            :checked="isActive"
            size="small"
            @change="handleChange"
          />
        </footer>
      </div>
    </template>
  </div>

  <SendTestMailModal v-if="openMailModal" v-model:open="openMailModal" :template="template" />
  <PortfoliosMailModal v-if="openPortfoliosMailModal" v-model:open="openPortfoliosMailModal" :template="template" />
</template>

<script setup lang="ts">
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  SendOutlined,
  WarningFilled,
  EyeOutlined,
} from "@ant-design/icons-vue";
import { useRouter } from "vue-router";
import { ref, computed, watch } from "vue";
import { message } from "ant-design-vue";

import { Template, TemplateState, TemplateType } from "@/@types/flows/emailTemplates";
import { emailTemplateService } from "@/app/flows/emailTemplates/services/EmailTemplate.service";
import { ProductName } from "@/app/shared/utils/constants";

import SendTestMailModal from "@/app/flows/emailTemplates/components/modals/SendTestMailModal.vue";
import PortfoliosMailModal from "@/app/flows/emailTemplates/components/modals/PortfoliosMailModal.vue";
import IconMegaphone from "@/app/common/components/atoms/IconMegaphone.vue";

const props = defineProps<{ create: boolean; template?: Template }>();

const emit = defineEmits<{ create: []; update: [] }>();

const router = useRouter();

const openMailModal = ref(false);
const openPortfoliosMailModal = ref(false);

const visiblePortfoliosPopover = ref(false);

const isActive = computed(() => {
  return props.template?.status === TemplateState.Active;
});

const handleClick = () => {
  if (!props.create) return;

  emit("create");
};

const handleEdit = () => {
  router.push(`/email/templates-email/${props.template?.id}`);
};

const handleEditPortfolio = () => {
  router.push(`/flows/templates-email/${props.template?.id}?tab=general`);
};

const handleChange = async () => {
  if (!props.template) return;

  try {
    await emailTemplateService.toggleState(props.template);

    emit("update");
  } catch(error) {
    const errorMessage = error?.getErrorMessage() || "Ha courrido un error";

    message.error(errorMessage);
  }
};

const handleCopy = async () => {
  if (!props.template) return;

  try {
    await emailTemplateService.makeCopy(props.template.id);

    emit("update");
  } catch(error) {
    const errorMessage = error?.getErrorMessage() || "Ha courrido un error";

    message.error(errorMessage);
  }
};

const handleDelete = async () => {
  if (!props.template) return;

  try {
    await emailTemplateService.delete(props.template.id);

    message.success("Se ha borrado la plantilla");

    emit("update");
  } catch(error) {
    const errorMessage = error?.getErrorMessage() || "Ha courrido un error";

    message.error(errorMessage);
  }
};

const handleViewPortfolios = () => {
  openPortfoliosMailModal.value = true;
  visiblePortfoliosPopover.value = false;
};

const countPortfolios = computed(() => {
  return props.template?.portfolios?.length;
});

const firstPortfolios = computed(() => {
  return props.template?.portfolios?.slice(0,5);
});

watch(visiblePortfoliosPopover,
  () => {
    if (countPortfolios.value == 0) visiblePortfoliosPopover.value = false;
  }
)
</script>

<style scoped lang="sass">
.divider
  margin: 12px 0
  background-color: $gray-5

.card
  padding: 16px 20px
  width: 100%
  background-color: $gray-2
  border: 1px solid $gray-5
  border-radius: 8px
  height: 204px
  display: flex
  flex-direction: column

  &.create
    border-style: dashed
    border-color: $blue-6
    background-color: $blue-1
    color: $primary
    cursor: pointer

    .create-container
      width: 100%
      height: 100%
      display: flex
      flex-direction: column
      gap: 2px
      align-items: center
      justify-content: center

      .icon
        font-size: 30px

      span
        font-size: 12px

  header
    display: flex
    justify-content: space-between
    margin-bottom: 10px

    .type
      font-size: 12px
      color: $text-secondary
      line-height: 20px

  .name
    font-weight: 600
    font-size: 14px
    color: $gray-9
    margin-bottom: 2px

  .description
    margin: 0
    color: $gray-7

  footer
    display: flex
    justify-content: space-between
    align-items: center

.actions
  display: flex
  flex-direction: column
  gap: 8px

  > button
    text-align: left

.tag-portfolio
  cursor: pointer

.associate
  text-decoration: underline
  cursor: pointer

.portfolios
  &__list
    padding-inline-start: 25px

.popover
  &__footer
    width: 100%
    display: flex
</style>
